import type { AllCMSPageFields } from '@/cms/fields/mappers/common'
import type {
  AppPageProps,
  RenderDocumentProps,
  RenderingStrategy,
  SlugPageProps,
} from '@/cms/rendering-strategy/types'
import type { SlugStaticProps } from '@/cms/types'
import { getCatalogueType } from '@/cms/catalogueTypeHelper'
import { getComponentIndex } from '@/cms/getComponentIndex'
import { pickComponentsFromIndex } from '@/cms/pickComponentsFromIndex'
import { getFonts } from '@/framework/react-styles'
import StyledComponentsRegistry from '@/framework/styled-components/styled-components-registry'
import { getBrandName } from '@/helpers/environment'
import HybridPage from '@/react-pages/HybridPage/HybridPage'
import { SetABNewRelicCustomAttribute } from '@/scopes/common/components/Scripts/setABNewRelicCustomAttribute'
import featureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient'
import AccommodationPage from '@/scopes/products/pages/AccommodationPage'
import { createConsoleLogger, PageComponent, Placeholder, UniformContextProvider } from '@uniformdev/next'
import * as React from 'react'

export function hybridRenderingStrategy(): RenderingStrategy {
  function renderDocument({ Head, Html, Main, NextScript, pageProps }: RenderDocumentProps) {
    const page = pageProps?.page

    const metadata = pageProps?.metadata
    const { name, template, fields } = page || {}
    const { bodyAttrs, htmlLang } = fields || {}
    const { machineName, staticFileName } = bodyAttrs || {}

    const brandName = getBrandName()
    const { primaryFont, secondaryFont } = getFonts(brandName)

    return (
      <Html lang={htmlLang}>
        <script
          // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: `
              window.sunwebgroup_legacy_design_system = {}
              window.sunwebgroup_legacy_design_system.REACT_REHYDRATION_MODE_ENABLED = true
            `,
          }}
        />
        {/* TODO review: are these metadata being used? */}
        <Head>
          {metadata?.title && <title>{metadata.title}</title>}
          {metadata?.faviconUrl && <link rel="shortcut icon" type="image/x-icon" href={metadata.faviconUrl} />}
        </Head>
        <body
          className={`o-block o-block--stretch l-site t-${staticFileName} ${primaryFont.variable} ${secondaryFont.variable}`}
          data-in-name={machineName}
          data-qa-page={name}
          data-qa-template={template}
        >
          <StyledComponentsRegistry>
            <Main />
            <NextScript />
          </StyledComponentsRegistry>
        </body>
      </Html>
    )
  }

  function renderApp({ Component, pageProps, ReactPage }: AppPageProps) {
    const locale = pageProps.config.locale

    if (shouldRenderHybridAccoPageApp(pageProps)) {
      return renderHybridAccoPageApp({ pageProps, Component, ReactPage })
    }

    return (
      <ReactPage
        translations={pageProps.translations}
        locale={locale}
        // TODO: This casting should be removed. In order to do so we need to remove exceptions for pages not using Uniform data
        cmsFields={pageProps?.cmsFields as AllCMSPageFields}
        config={pageProps.config}
        tokens={pageProps.tokens}
      >
        <HybridPage>
          <Component {...pageProps} />
        </HybridPage>
      </ReactPage>
    )
  }

  function renderPageComponent({ reactComponentsList, reactComponentParameters, ...pageProps }: SlugPageProps) {
    // TODO We're passing the url from uniform fields, but may be we should pass the url from the path
    const componentsIndex = getComponentIndex(
      getCatalogueType({ url: pageProps?.fields._url, catalogueList: { ...pageProps.config?.catalogues } }),
    )

    const selectedComponents = pickComponentsFromIndex(componentsIndex, {
      componentList: reactComponentsList,
      reactComponentParameters,
    })

    return (
      <UniformContextProvider logger={createConsoleLogger()} componentMap={selectedComponents}>
        <PageComponent {...pageProps}>
          {renderingContext => <Placeholder placeholderKey="/" renderingContext={renderingContext} />}
        </PageComponent>
      </UniformContextProvider>
    )
  }

  return {
    type: 'hybrid',
    renderDocument,
    renderApp,
    renderPageComponent,
  }
}

function shouldRenderHybridAccoPageApp(pageProps: SlugStaticProps): boolean {
  const pageTemplateName = pageProps.page.template
  const FF_ACTIVATE_FULL_REACT_PDP = featureFlagClient.isActive('ACTIVATE_FULL_REACT_PDP')

  return Boolean(
    pageProps.hasReactComponents && pageTemplateName === 'AccommodationPage' && !FF_ACTIVATE_FULL_REACT_PDP,
  )
}

function renderHybridAccoPageApp({
  pageProps,
  Component,
  ReactPage,
}: Pick<AppPageProps, 'pageProps' | 'Component' | 'ReactPage'>) {
  const locale = pageProps.config.locale

  return (
    <ReactPage
      translations={pageProps.translations}
      locale={locale}
      // TODO: This casting should be removed. In order to do so we need to remove exceptions for pages not using Uniform data
      cmsFields={pageProps?.cmsFields as AllCMSPageFields}
      config={pageProps.config}
      tokens={pageProps.tokens}
    >
      <SetABNewRelicCustomAttribute cookieEnabled={Boolean(pageProps?.enableAbTest)} />
      <AccommodationPage>
        <Component {...pageProps} />
      </AccommodationPage>
    </ReactPage>
  )
}
